import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { aciesDateFormat, downloadBlobFile, downloadFile } from '@shared/helpers';
import { OptionalField } from '@shared/models/fileupload';
import { GenericTableColumn, ISelectableData } from '@shared/models/generictablecolumn';
import { DocsService, findTemplateClashes, ITableTemplate, ITemplateExport, OutputType, TEMPLATE_AWS_ENV_IMPORT_EXPORT_PERMISSION } from '@shared/services/docs.service';
import { TemplateTransferService } from '@shared/services/export-import-services/template';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { TemplateValidatorService } from '@shared/services/filevalidators/templatevalidator.service';
import { UserService } from '@shared/services/user.service';
import { SelectSubMenuItem } from '@shared/utils/SelectSubMenuItemManager';
import { actionDefintion } from '@shared/utils/SimpleActionManager';
import { ITableFilter } from '@shared/utils/TableDataSource';
import { catchError, combineLatestWith, EMPTY, finalize, firstValueFrom, from, map, mergeMap, Observable, of, Subject, take, takeUntil, tap, toArray } from 'rxjs';
import { TemplateDeleteConfirmDialogContentComponent } from './confirm-dialog-content/confirmDialogContent.component';

/** How many templates the export asks the backend for at once - a list of thousands is fetched fifty at a time, not all at once. */
const EXPORT_CONCURRENCY = 50;

@Component({
	selector: 'app-templates',
	templateUrl: './templates.component.html',
	styleUrls: ['./templates.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TemplatesComponent {
	public isLoading: boolean = false;
	public showSearch: boolean = false;
	public totalCount: number;
	public pageSizes = [10, 20, 50, 100];
	public initialPageSize = 20;
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();
	public templateData: Observable<ISelectableData<ITableTemplate>[]>;

	activeKey = 'general.table.status.active';
	inactiveKey = 'general.table.status.inactive';

	public set templateFilterText(value: string | undefined) {
		if (value && value.length > 0) {
			this.templateFilter.filterString = value.trim().toLowerCase();
		} else {
			delete this.templateFilter.filterString;
		}

		// Trigger change detection by assigning a new object reference
		this.templateFilter = { ...this.templateFilter };
		console.log('Setting template filter text:', value);
	}

	public get templateFilterText(): string | undefined {
		return this.templateFilter.filterString || '';
	}

	public templateFilter: ITableFilter = {};

	setPropertyFilter(property: string, value: string | number | boolean | Date): void {
		if (!this.templateFilter.propertyFilter) {
			this.templateFilter.propertyFilter = {};
		}

		if (value !== undefined) {
			this.templateFilter.propertyFilter[property] = value;
		} else {
			delete this.templateFilter.propertyFilter[property];
		}

		// Trigger change detection by assigning a new object reference
		this.templateFilter = { ...this.templateFilter };
	}

	_showOnlyActive = true;
	_hasFetchedInactive = false;

	public set showOnlyActive(value: boolean) {
		this._showOnlyActive = value;

		this.setPropertyFilter('status', value ? this.activeKey : undefined);

		// If we are switching to inactive templates, we need to load the data
		if (this._hasFetchedInactive === false) {
			this.loadData();
		}
	}

	public get showOnlyActive(): boolean {
		return this._showOnlyActive;
	}

	private unsubscribe: Subject<void> = new Subject<void>();

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'docs.templates.table.name', dataKey: 'name' },
		{ name: 'docs.templates.table.description', dataKey: 'description' },
		{ name: 'docs.templates.table.version', dataKey: 'version' },
		{
			name: 'docs.templates.table.defaultversion',
			dataKey: 'defaultVersion',
		},
		{ name: 'docs.templates.table.created', dataKey: 'createdDate', sortKey: 'createdAtEpoch' },
		{
			name: 'docs.templates.table.status',
			dataKey: 'status',
			isTranslatable: true,
			sortAsShown: true,
		},
		{ name: 'docs.templates.table.tags', dataKey: 'tags' },
	];

	public regRowActions: actionDefintion[] = [
		{
			title: 'docs.templates.table.actions.edit',
			handler: 'uploadAssets',
		},
		{
			title: 'docs.templates.table.actions.delete.label',
			handler: 'deleteTemplate',
			filter: (rowData: ISelectableData<ITableTemplate>) => {
				return rowData.status === this.activeKey;
			},
		},
		{
			title: 'docs.templates.table.actions.deletePermanent.label',
			handler: 'deletePermanentTemplate',
			filter: (rowData: ISelectableData<ITableTemplate>) => {
				return rowData.status === this.inactiveKey;
			},
		},
		{
			title: 'docs.templates.table.actions.reactivate.label',
			handler: 'reactivateTemplate',
			filter: (rowData: ISelectableData<ITableTemplate>) => {
				return rowData.status === this.inactiveKey;
			},
		},
		{
			title: 'docs.templates.table.actions.download',
			handler: 'downloadAssets',
		},
	];

	public regSelectActions: SelectSubMenuItem[] = [
		{
			text: () => 'docs.templates.export.button',
			icon: () => 'acies_upload',
			click: (data) => this.handleTemplateExport(data),
		},
		{
			text: () => 'docs.templates.export.externalButton',
			icon: () => 'acies_upload',
			click: (data) => this.handleTemplateExport(data, true),
			permissions: () => [TEMPLATE_AWS_ENV_IMPORT_EXPORT_PERMISSION],
		},
		{
			text: () => 'docs.templates.table.actions.deleteMultiple.label',
			icon: () => 'acies_delete',
			click: (data) => this.deleteTemplate(data, false),
			filterMessage: 'docs.templates.table.actions.deleteMultiple.filterTooltip',
			filter: (rowData: ISelectableData<ITableTemplate>) => {
				return rowData.status === this.activeKey;
			},
			filterDisableMessage: 'docs.templates.table.actions.deleteMultiple.disableFilterTooltip',
			disableFilter: (rowData: ISelectableData<ITableTemplate>) => {
				// Disable action if no items are selected or all selected items are active
				return rowData.status === this.inactiveKey;
			},
		},
		{
			text: () => 'docs.templates.table.actions.deletePermanentMultiple.label',
			icon: () => 'acies_delete',
			click: (data) => this.deleteTemplate(data, true),
			filterMessage: 'docs.templates.table.actions.deletePermanentMultiple.filterTooltip',
			filter: (rowData: ISelectableData<ITableTemplate>) => {
				return rowData.status === this.inactiveKey;
			},
			filterDisableMessage: 'docs.templates.table.actions.deletePermanentMultiple.disableFilterTooltip',
			disableFilter: (rowData: ISelectableData<ITableTemplate>) => {
				// Disable action if no items are selected or all selected items are active
				return rowData.status === this.activeKey;
			},
		},
		{
			text: () => 'docs.templates.table.actions.reactivateMultiple.label',
			icon: () => 'acies_undo',
			click: (data) => this.reactivateTemplate(data),
			filterMessage: 'docs.templates.table.actions.reactivateMultiple.filterTooltip',
			filter: (rowData: ISelectableData<ITableTemplate>) => {
				return rowData.status === this.inactiveKey;
			},
			filterDisableMessage: 'docs.templates.table.actions.reactivateMultiple.disableFilterTooltip',
			disableFilter: (rowData: ISelectableData<ITableTemplate>) => {
				// Disable action if no items are selected or all selected items are active
				return rowData.status === this.activeKey;
			},
		},
	];

	public favoriteItems: SubMenuItem[] = [
		{
			click: () => void this.openUploadTemplateData(),
			icon: () => 'acies_add_alt',
			text: () => 'docs.templates.create.button',
		},
	];

	// Import goes in the overflow menu, where the export of the selected templates already is.
	public menuItems: SubMenuItem[] = [
		{
			click: () => void this.importTemplate(),
			icon: () => 'acies_download',
			text: () => 'docs.templates.import.button',
			permissions: () => ['docs:TemplateCreate'],
		},
	];

	constructor(
		private docsService: DocsService,
		public dialog: MatDialog,
		private injector: Injector,
		private userService: UserService,
		private notificationService: NotificationService,
		public changeDetection: ChangeDetectorRef,
		private translate: TranslateService,
	) {}

	ngOnInit(): void {
		this.loadData();
	}

	loadData(): void {
		// Set the flag to indicate whether we have fetched inactive templates
		this._hasFetchedInactive = !this._showOnlyActive;

		this.isLoading = true;
		this.templateData = this.docsService.getTemplateList(!this._showOnlyActive).pipe(
			tap((data) => {
				this.isLoading = true;
				this.countSubject$.next(data.result.length);
			}),
			map((data) => {
				const items: ISelectableData<ITableTemplate>[] = data.result.map((p) => ({
					id: p.self,
					name: p.name,
					description: p.description,
					self: p.self,
					versionself: p.version.self,
					version: p.meta.version,
					defaultVersion: p.meta.defaultVersion,
					tags: this.formatTagData(JSON.stringify(p.tags)),
					createdDate: aciesDateFormat(new Date(p.meta.createdAt * 1000), true, false),
					// the API counts seconds; kept in milliseconds, like every other list, for the sort
					createdAtEpoch: p.meta.createdAt * 1000,
					outputType: '',
					status: p.softDeleted ? this.inactiveKey : this.activeKey,
				}));
				return items;
			}),
			finalize(() => {
				this.isLoading = false;
			}),
		);
	}

	handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	async openUploadTemplateData(templateData: ITableTemplate = null) {
		if (this.isLoading) {
			return;
		}

		this.isLoading = true;
		const editMode = !!templateData;
		let existingOutput: any;
		try {
			if (templateData) {
				const getVersion = await firstValueFrom(this.docsService.getTemplateVersion(templateData.versionself).pipe(take(1)));
				const versionResult: any = getVersion.result;
				existingOutput = versionResult.output[0];
			}

			const defaultTopMarginValue = '100';
			const defaultBottomMarginValue = '140';
			const defaultPortraitOutputOrientationValue = this.translate.instant('docs.templates.dialog.orientation_portrait');
			const defaultOutputFormatValue = 'A4';
			const defaultOutputTypeValue = 'Pdf';
			const landscapeOutputOrientationValue = this.translate.instant('docs.templates.dialog.orientation_landscape');
			const outputOrientationValues = [defaultPortraitOutputOrientationValue, landscapeOutputOrientationValue];
			const outputFormatValues = ['A4', 'A3'];
			// Output type dropdown: allow any string value, including unknowns
			const outputTypeValues: Array<string> = [OutputType.pdf, OutputType.static, OutputType.xml];
			// If existingOutput has a type not in the list, add it
			if (existingOutput && existingOutput.type && !outputTypeValues.includes(existingOutput.type)) {
				outputTypeValues.push(existingOutput.type);
			}
			const resolveOutputType = (type?: string): string => {
				// Return the raw string, not the enum
				return type || OutputType.pdf;
			};
			const setOutputFieldState = (
				component: {
					optionalFields: OptionalField[];
					formData?: Record<string, { getRawValue: () => any }>;
				},
				outputType: string,
				readonly: boolean,
				useExistingValues: boolean,
				output = existingOutput,
			): void => {
				const resolvedOutputType = resolveOutputType(outputType);
				const isPdfOutput = resolvedOutputType.toLowerCase() === 'pdf';
				const currentValue = (fieldName: string, fallbackValue: string): string => {
					const value = component.formData?.[fieldName]?.getRawValue();
					return value == null || value === '' ? fallbackValue : value;
				};

				component.optionalFields.forEach((element: OptionalField) => {
					switch (element.name) {
						case 'template_outputformat':
							element.value = isPdfOutput ? (useExistingValues && output?.layout?.format ? output.layout.format : currentValue(element.name, defaultOutputFormatValue)) : defaultOutputFormatValue;
							// Only editable for PDF
							element.readonly = !isPdfOutput ? true : readonly;
							break;
						case 'template_outputlayout':
							element.value = isPdfOutput
								? useExistingValues && output?.layout?.landscape != null
									? output.layout.landscape
										? landscapeOutputOrientationValue
										: defaultPortraitOutputOrientationValue
									: currentValue(element.name, defaultPortraitOutputOrientationValue)
								: defaultPortraitOutputOrientationValue;
							element.readonly = !isPdfOutput ? true : readonly;
							break;
						case 'template_outputmargin_top':
							element.value = isPdfOutput ? (useExistingValues && output?.layout?.margins?.top != null ? output.layout.margins.top : currentValue(element.name, defaultTopMarginValue)) : defaultTopMarginValue;
							element.readonly = !isPdfOutput ? true : readonly;
							break;
						case 'template_outputmargin_bottom':
							element.value = isPdfOutput ? (useExistingValues && output?.layout?.margins?.bottom != null ? output.layout.margins.bottom : currentValue(element.name, defaultBottomMarginValue)) : defaultBottomMarginValue;
							element.readonly = !isPdfOutput ? true : readonly;
							break;
						case 'template_outputtype':
							element.value = resolvedOutputType;
							element.readonly = readonly;
							break;
					}
				});
			};

			this.docsService.template = templateData;
			const dialogRef = this.dialog.open(FileuploadComponent, {
				disableClose: true, // Luk ikke ved tryk på ESC eller backdrop - træls at miste indtastet data i alle felter
				width: '650px',
				data: {
					headingKey: editMode ? 'docs.templates.dialog.editheading' : 'docs.templates.dialog.heading',
					subHeadingKey: 'docs.templates.dialog.subheading',
					allowedFileTypes: ['.zip'],
					onlyShowCloseWhenFileExists: false,
					validatorService: this.injector.get(TemplateValidatorService),
					mode: editMode ? 'edit' : 'create',
					keepFormDataOnUpload: true,
					optionalFields: [
						{
							name: 'template_name',
							titleKey: 'docs.templates.dialog.name',
							mandatory: true,
							value: editMode && templateData['name'] ? templateData['name'] : '',
							validatorPattern: /^[\w\-æøå ]+$/i,
							errorMessage: 'docs.templates.dialog.inputError',
						},
						{
							name: 'template_description',
							titleKey: 'docs.templates.dialog.description',
							mandatory: false,
							value: editMode && templateData['description'] ? templateData['description'] : '',
							validatorPattern: /^[\w\-æøå ]+$/i,
							errorMessage: 'docs.templates.dialog.inputError',
						},
						{
							name: 'template_version',
							titleKey: 'docs.templates.dialog.version',
							mandatory: true,
							type: 'number',
							class: 'w50',
							readonly: true,
							alwaysUpdate: true,
							value: editMode && templateData['version'] ? templateData['version'] : '1',
						},
						{
							name: 'template_defaultversion',
							titleKey: 'docs.templates.dialog.defaultversion',
							mandatory: true,
							type: 'number',
							class: 'w50',
							value: editMode && templateData['defaultVersion'] ? templateData['defaultVersion'] : '1',
							minimumValue: 1,
						},
						{
							name: 'template_outputtype',
							titleKey: 'docs.templates.dialog.outputtype',
							mandatory: true,
							readonly: editMode,
							values: outputTypeValues,
							value: existingOutput ? existingOutput.type || defaultOutputTypeValue : defaultOutputTypeValue,
						},
						{
							name: 'template_outputformat',
							titleKey: 'docs.templates.dialog.outputformat',
							mandatory: true,
							readonly: editMode || resolveOutputType(existingOutput?.type) !== OutputType.pdf,
							values: outputFormatValues,
							class: 'w25',
							value: existingOutput?.layout?.format ? existingOutput.layout.format : defaultOutputFormatValue,
						},
						{
							name: 'template_outputlayout',
							titleKey: 'docs.templates.dialog.outputlayout',
							mandatory: true,
							readonly: editMode || resolveOutputType(existingOutput?.type) !== OutputType.pdf,
							values: outputOrientationValues,
							class: 'w25',
							value: existingOutput?.layout?.landscape ? landscapeOutputOrientationValue : defaultPortraitOutputOrientationValue,
						},
						{
							name: 'template_outputmargin_top',
							titleKey: 'docs.templates.dialog.outputmargin_top',
							mandatory: true,
							readonly: editMode || resolveOutputType(existingOutput?.type) !== OutputType.pdf,
							type: 'number',
							class: 'w25',
							value: existingOutput?.layout?.margins?.top ? existingOutput.layout.margins.top : '100',
							minimumValue: 0,
						},
						{
							name: 'template_outputmargin_bottom',
							titleKey: 'docs.templates.dialog.outputmargin_bottom',
							mandatory: true,
							readonly: editMode || resolveOutputType(existingOutput?.type) !== OutputType.pdf,
							type: 'number',
							class: 'w25',
							value: existingOutput?.layout?.margins?.bottom ? existingOutput.layout.margins.bottom : '140',
							minimumValue: 0,
						},
						{
							name: 'template_tags',
							titleKey: 'docs.templates.dialog.tags',
							mandatory: true,
							value: editMode && templateData['tags'] ? templateData['tags'] : '',
						},
					] as OptionalField[],
					selectValueChange() {
						const selectedOutputType = resolveOutputType(this.formData['template_outputtype']?.getRawValue());
						setOutputFieldState(this, selectedOutputType, editMode, false);
						this.applyFormChanges();
					},
					processFileHook() {
						const documentService = this.injector.get(DocsService);
						const selectedOutputType = resolveOutputType(this.formData['template_outputtype']?.getRawValue());
						setOutputFieldState(this, selectedOutputType, false, false);
						this.optionalFields.forEach((element: OptionalField) => {
							if (element.name === 'template_version' && documentService?.template?.versionself.length > 0) {
								element.value = documentService.template.version + 1;
							}
						});

						return true;
					},
					removeFileHook() {
						const documentService = this.injector.get(DocsService);
						setOutputFieldState(this, existingOutput ? resolveOutputType(existingOutput.type) : defaultOutputTypeValue, editMode, true);
						this.optionalFields.forEach((element: OptionalField) => {
							if (element.name === 'template_version' && documentService?.template?.versionself.length > 0) {
								element.value = documentService.template.version;
							}
						});

						return true;
					},
					initHook() {
						const documentService = this.injector.get(DocsService);
						if (documentService.template && documentService.template.versionself.length > 0) {
							documentService
								.getTemplateVersion(documentService.template.versionself)
								.pipe(take(1))
								.subscribe((data) => {
									if (data.result.output.length > 0) {
										const output = data.result.output[0];
										const outputType = resolveOutputType(output.type);
										this.formData['template_outputtype'].setValue(outputType);
										setOutputFieldState(this, outputType, true, true, output);
										this.applyFormChanges();
									}
								});
						}
						return true;
					},
				},
			});
			this.isLoading = false;

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.unsubscribe))
				.subscribe((result) => {
					if (result != null) {
						this.isLoading = true;
						setTimeout(() => {
							this.loadData();
						}, 1000);
					} else {
						this.isLoading = false;
					}
				});
		} catch (error) {
			this.isLoading = false;
			throw error;
		}
	}

	formatTagData(tags: string): string {
		const data: any[] = JSON.parse(tags);
		let result = '';

		data.forEach((tag) => {
			result += `${tag.key}=${tag.value}, `;
		});

		if (result.length > 0) {
			result = result.substring(0, result.length - 2);
		}
		return result;
	}

	handleRowMenuAction(data: object): void {
		switch (data['action'].handler) {
			case 'uploadAssets':
				void this.openUploadTemplateData(data['data']);
				break;
			case 'downloadAssets':
				this.downloadTemplate(data['data']);
				break;
			case 'deleteTemplate':
				this.deleteTemplate(data['data'], false);
				break;
			case 'deletePermanentTemplate':
				console.log('Delete permanent template', data['data']);
				this.deleteTemplate(data['data'], true);
				break;
			case 'reactivateTemplate':
				console.log('Delete permanent template', data['data']);
				this.reactivateTemplate(data['data']);
				break;
			default:
				break;
		}
	}

	deleteTemplate(data: ITableTemplate | ITableTemplate[], permanent: boolean = false): void {
		if (!Array.isArray(data)) data = [data];

		const path = permanent ? 'deletePermanent' : 'delete';

		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				heading: this.translate.instant(`docs.templates.${path}.heading`),
				contentComponent: TemplateDeleteConfirmDialogContentComponent,
				contentContext: { templates: data, permanent },
				okButtonText: this.translate.instant(`docs.templates.${path}.button`),
				cancelButtonText: this.translate.instant(`docs.templates.${path}.cancel`),
				okReturnData: data,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(
				take(1),
				mergeMap((result: ITableTemplate[]) => {
					// If the user cancelled the dialog, result will be null or empty
					if (!result || result.length === 0) {
						return of();
					}
					this.isLoading = true;
					return from(result.filter((item) => item !== null));
				}),
				mergeMap((result) => this.docsService.deleteTemplate(result.self, permanent)),
			)
			.subscribe({
				complete: () => {
					this.isLoading = false;
					this.loadData();
				},
				error: (error) => {
					console.error('Error deleting templates', error);
					this.isLoading = false;
				},
			});
	}

	reactivateTemplate(data: ITableTemplate | ITableTemplate[]): void {
		if (!Array.isArray(data)) data = [data];

		from(data)
			.pipe(mergeMap((result) => this.docsService.reactivateTemplate(result.self)))
			.subscribe({
				complete: () => {
					this.isLoading = false;
					this.loadData();
				},
				error: (error) => {
					console.error('Error reactivating templates', error);
					this.isLoading = false;
				},
			});
	}

	downloadTemplate(data: ITableTemplate): void {
		this.docsService
			.getDownloadUrl(data.versionself)
			.pipe(take(1))
			.subscribe((result) => {
				const uploadUrl = result.result.url;
				downloadFile(uploadUrl);
			});
	}

	// What is exported is what is ticked in the table - the export items of the menu are only there to be used with a selection, and hand it over. Without one there is nothing to export, and no empty file to download.
	handleTemplateExport(data: ISelectableData<ITableTemplate>[], external: boolean = false): void {
		if (!data?.length) {
			return;
		}

		// The spinner runs from here until the file is downloaded or the export stops - the export is a request per template.
		this.isLoading = true;

		// A template is matched on its name, description and tags together when imported, so a selected template that another one also is cannot be exported.
		this.docsService
			.getTemplateList()
			.pipe(take(1))
			.subscribe({
				next: ({ result }) => {
					const clashes = findTemplateClashes(data, result);
					if (clashes.length > 0) {
						this.isLoading = false;
						this.showNamesInDialog('docs.templates.export.errors.duplicate-names', clashes);
						return;
					}
					this.exportTemplates(data, external);
				},
				error: () => {
					this.isLoading = false;
					this.notifyExportFailed();
				},
			});
	}

	private notifyExportFailed(): void {
		this.notificationService.notify(['docs.templates.export.notification.error.header'], ['docs.templates.export.notification.error.content'], 'error');
	}

	// A list of names is for a dialog to hold, not a notification. The dialog keeps the line breaks of its message.
	private showNamesInDialog(introKey: string, names: string[]): void {
		const list = names.map((name) => `• ${name}`).join('\n');
		this.dialog.open(TransferErrorPopupComponent, {
			width: '450px',
			data: { message: `${this.translate.instant(introKey)}\n${list}` },
		});
	}

	// What the file holds of a template - and nothing else: its `self` id names it in this environment only, so it stays out of the file. The export keeps it on the template until then, to match each one to its download link.
	private toFileTemplate(template: ITemplateExport & { self: string }): ITemplateExport {
		return {
			templateAssetKey: template.templateAssetKey,
			name: template.name,
			description: template.description,
			tags: template.tags,
			input: template.input,
			output: template.output,
		};
	}

	private exportTemplates(data: ISelectableData<ITableTemplate>[], external: boolean): void {
		of(...data)
			.pipe(
				mergeMap((templateData) => {
					return this.docsService.getTemplateVersion(templateData.versionself).pipe(
						take(1),
						map((data) => {
							return {
								latestVersion: data.result,
								templateData,
							};
						}),
						catchError((error) => {
							console.log('Failed to get template version');
							throw error;
						}),
					);
				}, EXPORT_CONCURRENCY),
				combineLatestWith(this.userService.account$.pipe(take(1))),
				map(([data, account]) => {
					const { latestVersion, templateData } = data;
					const templateAssetKey = `accounts/${account.id}/${templateData.versionself.slice(6)}/assets/assets.zip`;
					return {
						templateAssetKey,
						name: templateData.name,
						description: templateData.description,
						tags: templateData.tags.replace(/, /g, ';'),
						self: templateData.self,
						input: latestVersion.input,
						output: latestVersion.output,
					} as ITemplateExport & { self: string };
				}),
				toArray(),
				mergeMap((templates) => (external ? this.withPresignedAssetUrls(templates, data) : of(templates))),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe({
				next: (templates) => {
					// download the json
					downloadBlobFile(new Blob([JSON.stringify(templates.map((template) => this.toFileTemplate(template)))]), 'templates.json');
				},
				error: (error) => {
					console.log('Failed to export template', error);
					this.notifyExportFailed();
				},
			});
	}

	// The asset key built above is a key in this environment's bucket - another environment can only import from a presigned url.
	private withPresignedAssetUrls(templates: (ITemplateExport & { self: string })[], data: ISelectableData<ITableTemplate>[]): Observable<(ITemplateExport & { self: string })[]> {
		const versionByTemplate = new Map(data.map((row) => [row.self, row.versionself]));

		return this.docsService.getSignedUrls([...versionByTemplate.values()]).pipe(
			mergeMap((urlByVersion) => {
				// A template is never exported without its asset: when the backend gave no url for some, nothing is exported and the dialog names them, in the order they were selected.
				const withoutUrl = data.filter((row) => !urlByVersion.has(row.versionself)).map((row) => row.name);
				if (withoutUrl.length > 0) {
					this.showNamesInDialog('docs.templates.export.errors.signed-url-missing', withoutUrl);
					return EMPTY;
				}
				return of(templates.map((template) => ({ ...template, templateAssetKey: urlByVersion.get(versionByTemplate.get(template.self)) })));
			}),
		);
	}

	importTemplate() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'docs.templates.import.title',
				subHeadingKey: 'docs.templates.import.subtitle',
				confirmButtonKey: 'docs.templates.import.button',
				notificationHeader: 'docs.templates.import.notification.success.header',
				notificationContent: 'docs.templates.import.notification.success.content',
				notificationErrorHeader: 'docs.templates.import.notification.error.header',
				notificationErrorContent: 'docs.templates.import.notification.error.content',
				notificationClientErrorHeader: 'docs.templates.import.notification.clientError.header',
				notificationClientErrorContent: 'docs.templates.import.notification.clientError.content',
				detailedClientErrorMessage: 'docs.templates.import.detailed-client-error-message',
				allowedFileTypes: ['.json'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.injector.get(TemplateTransferService),
				optionalFields: [
					{
						name: 'overwrite',
						titleKey: 'docs.templates.import.overwrite',
						type: 'checkbox',
						value: false,
					},
				],
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			this.loadData();
		});
	}
}
